import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Button, Col, Row, Modal, Form, Card, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ReduxState } from "../../../redux";
import { resumeJob, savePreferenceServer } from "../../../docuclipper/api";
import { setJobData } from "../../../redux/reducers/JobData";
import { createAlert } from "../../../redux/reducers/Alerts";
import { AutomatedPagePurchases } from "../../Pages/Account/AutomatedPagePurchases";
import { openModal } from "../../../redux/reducers/ModalManager";
import { useShowOneOff } from "../Analyze/AnalyzeTransactionsTable/hooks";

// Feature flag for overages
const ENABLE_OVERAGES = true;

export const JobOutOfPages = ({ job }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState("5000");

  const isContractAdmin = useSelector(
    (state: ReduxState) => state.User.user?.isContractAdmin
  );

  const { subscription } = useSelector(
    (state: ReduxState) => state.Subscription
  );

  const { user } = useSelector((state: ReduxState) => state.User);

  const neededPages = useSelector(
    (state: ReduxState) => state.JobData.job?.totalJobs
  );

  const userPages = Number(user?.credits) + Number(user?.purchasedCredits);
  // console.log({ userPages, neededPages });
  React.useEffect(() => {
    dispatch(
      createAlert({
        timeout: 0,
        type: "error",
        messageWithUpdate: `This job requires ${neededPages?.toLocaleString()} pages, but your current plan only has ${userPages?.toLocaleString()} pages available`,
        message: "",
      })
    );
  }, [neededPages, userPages]);

  const onResume = () => {
    if (!job) return;
    resumeJob(job.id)
      .then((rsp) => {
        dispatch(setJobData({ job: rsp, progress: null }));
        dispatch(
          createAlert({
            message: "Job resumed successfully",
            timeout: 0,
            type: "success",
          })
        );
      })
      .catch((err) => {
        dispatch(
          createAlert({
            message: "Error resuming job",
            timeout: 0,
            type: "error",
          })
        );
      });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await savePreferenceServer({
        key: "overageConfig",
        value: JSON.stringify({
          enabled: true,
          numPages: parseInt(numPages, 10),
        }),
      });

      setShowModal(false);
      onResume();
    } catch (error) {
      console.error("Error saving overage configuration:", error);
      dispatch(
        createAlert({
          message: "Error configuring overages",
          timeout: 0,
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (pages: number) => {
    setIsLoading(true);
    try {
      // After successful purchase, resume the job
      onResume();
    } catch (error) {
      console.error("Error handling purchase:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyPages = () => {
    dispatch(openModal("buyPages", { type: "oneoff" }));
  };

  const showOneOff = useShowOneOff();

  return (
    <div style={{ maxWidth: "80vw" }}>
      <Row className="text-center mb-4">
        <Col>
          <div className="h2 m-4 d-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon="times" color="red" />{" "}
            <span className="ml-2">
              Page Limit Reached ({userPages?.toLocaleString()} pages available,{" "}
              {neededPages?.toLocaleString()} pages needed)
            </span>
            <Button variant="light" className="ml-3" onClick={onResume}>
              Resume Job
            </Button>
          </div>
        </Col>
      </Row>

      {/* Main CTA */}
      <Row className="justify-content-center mb-5">
        <Col md={8} className="text-center">
          {/* <h3 className="mb-4">Recommended Solution</h3> */}
          <Card className="mb-4">
            <Card.Header /*className="bg-primary text-white"*/>
              <div className="d-flex align-items-center justify-content-center">
                <h4 className="mb-0">Upgrade Your Plan</h4>
                <Badge
                  variant="primary"
                  className="ml-2"
                  style={{ fontSize: "0.9rem", padding: "0.35em 0.6em" }}
                >
                  Recommended
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={7} className="text-left">
                  <h5>Why Upgrade?</h5>
                  <ul className="mb-0">
                    <li>Lower cost per page</li>
                    <li>Priority support</li>
                    <li>Advanced features</li>
                  </ul>
                </Col>
                <Col
                  md={5}
                  className="d-flex align-items-center justify-content-center"
                >
                  <Button
                    size="lg"
                    variant="primary"
                    className="px-4"
                    onClick={() => history.push("/account/upgrade")}
                  >
                    View Plan Options
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Secondary Options */}
      <Row className="justify-content-center">
        <Col md={8}>
          <h4 className="text-center mb-4">Alternative Options</h4>
          <Card>
            <Card.Body>
              {/* <h5 className="mb-3">Purchase Additional Pages</h5> */}
              <Row>
                {showOneOff &&
                  subscription &&
                  subscription.status === "active" && (
                    <Col md={6} className="mb-4 mb-md-0">
                      <div className="h-100 d-flex flex-column">
                        <h6>One-time Purchase</h6>
                        <p className="flex-grow-1">
                          Buy additional pages as needed, with no commitment.
                        </p>
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={handleBuyPages}
                        >
                          Buy Pages Now
                        </Button>
                      </div>
                    </Col>
                  )}
                {subscription && subscription.status === "active" && (
                  <Col md={6}>
                    <div className="h-100 d-flex flex-column">
                      <h6>Auto-Refill</h6>
                      <p className="flex-grow-1">
                        Set up automatic refills when your pages run low.
                      </p>
                      <div style={{ minWidth: 0 }}>
                        <AutomatedPagePurchases onPurchase={handlePurchase} />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Legacy overages modal */}
      {ENABLE_OVERAGES && (
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <div className="page-title-icon">
              <span>Configure Legacy Overages</span>
            </div>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="numPages">
              <Form.Label>Select Overage Package</Form.Label>
              <Form.Control
                as="select"
                value={numPages}
                onChange={(e) => setNumPages(e.target.value)}
                disabled={isLoading}
              >
                <option value="5000">5,000 pages (6 cents/page)</option>
                <option value="2000">2,000 pages (8 cents/page)</option>
                <option value="1000">1,000 pages (16 cents/page)</option>
                <option value="500">500 pages (20 cents/page)</option>
                <option value="250">250 pages (24 cents/page)</option>
              </Form.Control>
            </Form.Group>
            <p>
              When you run out of pages, DocuClipper will automatically add{" "}
              {parseInt(numPages).toLocaleString()} pages for $
              {(
                parseInt(numPages) *
                ({
                  "5000": 0.06,
                  "2000": 0.08,
                  "1000": 0.16,
                  "500": 0.2,
                  "250": 0.24,
                }[numPages] ?? 0.06)
              ).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isLoading}>
              Enable Legacy Overages
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
